import type React from 'react'
import type { TFunction } from 'react-i18next'

import type { FailedRefundTransaction } from '~/api/graphql/generated/types'

import { TransactionFailureReasonId } from '../../../add-payment-dialog-v2/utils/paymentErrorMapping'
import { extractRefundFailureReasonId } from './refundErrorMapping'

interface InitiateLinkedRefundTransactionDependencies {
  dispatch: React.Dispatch<any>
  initiateRefundTransactionMutation: (options: {
    variables: {
      input: {
        amount: number
        externalDeviceId: string | null
        originalPaymentId: string
        refundInvoiceId: string
      }
    }
  }) => Promise<any>
  refundAmount: number
  refundInvoiceId: string
  selectedPaymentId: string
  setIsLoading: (isLoading: boolean) => void
  t: TFunction<'Dialogs'>
}

/**
 * Handles the linked refund transaction mutation with error handling.
 * Unlike unlinked refunds, this does not pass an externalDeviceId and does not
 * update the RefundFlowState or set isLoading to false on success.
 * On success, it sets the transaction ID and enables subscription listening.
 */
export const initiateLinkedRefundTransaction = async (
  deps: InitiateLinkedRefundTransactionDependencies,
): Promise<void> => {
  const {
    dispatch,
    initiateRefundTransactionMutation,
    refundAmount,
    selectedPaymentId,
    refundInvoiceId,
    setIsLoading,
    t,
  } = deps

  try {
    const result = await initiateRefundTransactionMutation({
      variables: {
        input: {
          amount: refundAmount,
          originalPaymentId: selectedPaymentId,
          refundInvoiceId,
          // Linked refunds do not require an external device ID
          externalDeviceId: null,
        },
      },
    })

    if (
      !result?.data?.initiateRefundTransaction ||
      result.data?.initiateRefundTransaction?.error?.failureReason ||
      result.errors
    ) {
      dispatch({ type: 'CANCEL_TRANSACTION' })
      dispatch({
        type: 'SET_REFUND_ERROR',
        payload: {
          message: t(
            'Dialogs:REFUND_DIALOG_V2.ERRORS.REFUND_INITIATION_FAILURE_LINKED',
          ),
          severity: 'error',
          status: 500,
        },
      })
      // If failure reason is UNKNOWN, do not fall back to unlinked flow
      if (
        result.data?.initiateRefundTransaction?.error?.failureReason &&
        extractRefundFailureReasonId(
          result.data?.initiateRefundTransaction as FailedRefundTransaction,
        ) !== TransactionFailureReasonId.UNKNOWN
      ) {
        dispatch({ type: 'SET_LINKED', payload: false })
        dispatch({ type: 'SET_UNLINKED', payload: true })
        dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
      }
      setIsLoading(false)
    } else {
      dispatch({
        type: 'SET_TRANSACTION_ID',
        payload: result.data.initiateRefundTransaction.id,
      })
      dispatch({
        type: 'SET_LINKED_REFUND_LISTENING',
        payload: true,
      })
    }
  } catch (err) {
    dispatch({ type: 'CANCEL_TRANSACTION' })
    dispatch({
      type: 'SET_REFUND_ERROR',
      payload: {
        message: t(
          'Dialogs:REFUND_DIALOG_V2.ERRORS.REFUND_INITIATION_FAILURE_LINKED',
        ),
        severity: 'error',
        status: 500,
      },
    })
    dispatch({ type: 'SET_LINKED', payload: false })
    dispatch({ type: 'SET_UNLINKED', payload: true })
    dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
    setIsLoading(false)
  }
}
